import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { RREFormStatus } from 'src/app/real-estate-form-dashboard/common/real-estate-form-dashboard.types';


@Component({
  selector: 'app-real-estate-form-activity',
  templateUrl: './real-estate-form-activity.component.html',
  styleUrls: ['./real-estate-form-activity.component.scss']
})
export class RealEstateFormActivityComponent implements OnChanges {
  @Input() formActivityVisible: boolean = false;
  @Input() reviewMode: boolean = false;
  @Input() formActivity: any[] = [];
  @Input() showDerivationError: boolean = false;
  @Input() projectData: any;
  @Input() formStatus: string;
  @Input() userEmail: string;
  @Input() formOwner: any;
  @Input() currentAssignment: any;
  @Input() isReadyForDOAApproval: boolean = false;
  @Input() isReadyForITSpareApproval: boolean = false;
  @Input() reviewComments: string = "";
  @Input() readyToAddAccountingCodeBlock: boolean = false;
  @Input() emailRecipients: any;

  @Output() toggleActivity = new EventEmitter<void>();
  @Output() approve = new EventEmitter<{
    isReject?: boolean;
    accountingCodeBlock?: Record<string, any>;
  }>();
  @Output() reviewCommentsChange = new EventEmitter<string>();
  @Output() accounting = new EventEmitter<void>();

  showAccountingForm: boolean = false;
  accountingFormGroup: FormGroup;

  formStatusTimeline = {
    readyForReview: false,
    beingReviewed: false,
    youRejectedForm: false,
    formRejected: false,
    youApprovedForm: false,
    formCompleted: false,
  };

  constructor(private fb: FormBuilder) {
    this.accountingFormGroup = this.fb.group({
      projectId: [''],
      projectDescription: [''],
      activityId: [''],
      account: [''],
      processCode: [''],
      capitalOperatingUnit: [''],
      omOperatingUnit: [''],
      responsibilityCenter: [''],
      businessUnit: ['']
    });
  }

  /** Returns true when a previously saved codeblock has at least one real value. */
  hasPersistedCodeBlock(): boolean {
    const project = this.projectData && this.projectData.project;
    const acb = project && project.accountingCodeBlock;
    return !!(acb && (
      acb.activityId || acb.account || acb.processCode ||
      acb.capitalOperatingUnit || acb.omOperatingUnit ||
      acb.responsibilityCenter || acb.businessUnit
    ));
  }

  /** Exposes the saved codeblock for the template. */
  get savedCodeBlock(): any {
    return (
      this.projectData &&
      this.projectData.project &&
      this.projectData.project.accountingCodeBlock
    ) || {};
  }

  ngOnChanges(): void {
    this.checkStatus();
    this.setAccountingFormValues();
    // Once a codeblock is persisted or the form is approved, never show the entry form again
    if (this.hasPersistedCodeBlock() || this.formStatus === RREFormStatus.APPROVED) {
      this.showAccountingForm = false;
    }
  }

  setAccountingFormValues(): void {
    if (
      this.projectData &&
      this.projectData.project &&
      this.projectData.project.detailForm
    ) {
      const projectId = this.projectData.project.detailForm.projectId;
      const projectDescription =
        this.projectData.project.detailForm.projectDescription;
      // Load all persisted codeblock values so they show pre-filled on re-open
      const acb = this.projectData.project.accountingCodeBlock || {};

      this.accountingFormGroup.patchValue({
        projectId: projectId || '',
        projectDescription: projectDescription || '',
        activityId: acb.activityId || '',
        account: acb.account || '',
        processCode: acb.processCode || '',
        capitalOperatingUnit: acb.capitalOperatingUnit || '',
        omOperatingUnit: acb.omOperatingUnit || '',
        responsibilityCenter: acb.responsibilityCenter || '',
        businessUnit: acb.businessUnit || '',
      });
    }
  }

  toggleFormActivity() {
    // Always reset accounting form on close so it never re-opens in entry state
    this.showAccountingForm = false;
    this.toggleActivity.emit();
  }

  approveForm(isReject?: boolean) {
    // Reuse persisted codeblock on update approvals; fall back to form values for first-time save
    const accountingCodeBlock = this.hasPersistedCodeBlock()
      ? this.projectData.project.accountingCodeBlock
      : this.accountingFormGroup.value;
    this.approve.emit({
      isReject: isReject,
      accountingCodeBlock: accountingCodeBlock,
    });
  }

  onReviewCommentsChange(value: string) {
    this.reviewCommentsChange.emit(value);
  }

  onAccountingClick() {
    if (this.hasPersistedCodeBlock()) {
      // Codeblock already saved — skip re-entry and approve directly
      this.onSaveAccounting();
    } else {
      this.showAccountingForm = true;
      this.accounting.emit();
    }
  }

  onCancelAccounting() {
    this.showAccountingForm = false;
  }

  onSaveAccounting() {
    this.showAccountingForm = false;
    const accountingCodeBlock = this.hasPersistedCodeBlock()
      ? this.projectData.project.accountingCodeBlock
      : this.accountingFormGroup.value;
    this.approve.emit({
      isReject: false,
      accountingCodeBlock: accountingCodeBlock,
    });
  }

  checkStatus() {
    if (this.projectData) {
      this.formStatusTimeline.readyForReview = this.checkReadyForReview();
      this.formStatusTimeline.beingReviewed = this.checkBeingReviewed();
      this.formStatusTimeline.youRejectedForm = this.checkYouRejectedForm();
      this.formStatusTimeline.formRejected = this.checkFormRejected();
      this.formStatusTimeline.youApprovedForm = this.checkYouApprovedForm();
      this.formStatusTimeline.formCompleted = this.checkFormCompleted();
    }
  }

  checkReadyForReview() {
    const iTSpacEmailAllowList = this.emailRecipients.realEstateContacts.formActivityAllowList.map(contact => contact.email);
    // Only block approval for codeblock entry when it has never been saved before
    if (this.readyToAddAccountingCodeBlock && !this.hasPersistedCodeBlock()) return false;
    const isAllow = iTSpacEmailAllowList.includes(this.userEmail);
    return (
      (this.formStatus === RREFormStatus.UNDER_REVIEW &&
        this.isCurrentApprover()) ||
      (this.isReadyForDOAApproval && (this.isCurrentApprover() || isAllow)) ||
      (this.isReadyForITSpareApproval && (this.isCurrentApprover() || isAllow))
    );
  }

  checkBeingReviewed() {
    const isPM = this.formOwner && this.formOwner.mail === this.userEmail;
    return (
      (this.formStatus === RREFormStatus.UNDER_REVIEW && isPM) ||
      (this.isReadyForDOAApproval && isPM) ||
      (this.isReadyForITSpareApproval && isPM)
    );
  }

  checkYouRejectedForm() {
    return (
      this.projectData.project.status === RREFormStatus.REJECTED &&
      this.projectData.project.workflow.find(
        (w) =>
          w.status === RREFormStatus.REJECTED &&
          w.assignedBy.mail === this.userEmail,
      ) &&
      !this.checkReadyForReview()
    );
  }

  checkFormRejected() {
    return (
      this.projectData.project.status === RREFormStatus.REJECTED &&
      !this.checkYouRejectedForm()
    );
  }

  checkYouApprovedForm() {
    return (
      this.projectData.project.status === RREFormStatus.APPROVED &&
      this.projectData.project.workflow.find(
        (w) =>
          w.status === RREFormStatus.APPROVED &&
          w.assignedBy.mail === this.userEmail,
      ) &&
      !this.checkReadyForReview()
    );
  }

  checkFormCompleted() {
    // Never show ACCOUNTING button once form is approved or codeblock was already saved
    if (this.formStatus === RREFormStatus.APPROVED) return false;
    if (this.hasPersistedCodeBlock()) return false;

    const iTSpacEmailAllowList = this.emailRecipients.realEstateContacts.formActivityAllowList.map(contact => contact.email);
    const isAllow = iTSpacEmailAllowList.includes(this.userEmail);
    return (
      this.readyToAddAccountingCodeBlock &&
      (this.isCurrentApprover() || isAllow)
    );
  }

  isCurrentApprover() {
    return (
      this.userEmail &&
      this.currentAssignment &&
      this.currentAssignment.mail &&
      this.userEmail.toLowerCase() === this.currentAssignment.mail.toLowerCase()
    );
  }
}
